import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowUpRight, BriefcaseBusiness, MapPin, Search, Star } from 'lucide-react';

import { ApiError, request } from '@/api/client';
import type { PublicJobFilters, Vacancy, VacancyPage } from '@/api/schema';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { cx } from '@/lib/cx';
import { useDebounced } from '@/lib/useDebounced';
import { PublicApplicationForm } from '../components/PublicApplicationForm';
import { PublicCareersLayout } from '../components/PublicCareersLayout';
import { JobLinkActions } from '../components/JobLinkActions';
import { VacancyDetails } from '../components/VacancyDetails';
import styles from './PublicJobsPage.module.css';

export function PublicJobsPage() {
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [highlightedOnly, setHighlightedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const debounced = useDebounced(search, 250);
  const filters = {
    page,
    search: debounced,
    country,
    industry,
    employment_type: employmentType,
    highlighted: highlightedOnly ? true : undefined,
  };
  const hasFilters = Boolean(
    search.trim() || country || industry || employmentType || highlightedOnly,
  );
  const showHighlights = !hasFilters && page === 1;
  const options = useQuery({
    queryKey: ['public-jobs', 'filters'],
    queryFn: () =>
      request<PublicJobFilters>('/api/v1/public/jobs/filters', { anonymous: true }),
  });
  const jobs = useQuery({
    queryKey: ['public-jobs', 'list', filters],
    queryFn: () =>
      request<VacancyPage>('/api/v1/public/jobs', {
        anonymous: true,
        query: { ...filters, per_page: 12 },
      }),
  });
  const highlights = useQuery({
    queryKey: ['public-jobs', 'highlights'],
    queryFn: () =>
      request<VacancyPage>('/api/v1/public/jobs', {
        anonymous: true,
        query: { highlighted: true, per_page: 3 },
      }),
    enabled: showHighlights,
  });
  const clearFilters = () => {
    setSearch('');
    setCountry('');
    setIndustry('');
    setEmploymentType('');
    setHighlightedOnly(false);
    setPage(1);
  };
  return (
    <PublicCareersLayout>
      <section className={styles.hero}>
        <span className={styles.eyebrow}>YOUR NEXT CHAPTER</span>
        <h1>
          Find your next
          <br />
          opportunity overseas.
        </h1>
        <p>
          Explore open roles and apply directly. Your application goes straight to our
          recruitment team.
        </p>
      </section>
      <section className={styles.filterPanel} aria-label="Filter jobs">
        <Field label="Search jobs">
          {(ids) => (
            <Input
              {...ids}
              icon={<Search size={18} />}
              type="search"
              placeholder="Job title or location"
              maxLength={200}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          )}
        </Field>
        <div className={styles.filterGrid}>
          <Field label="Country">
            {(ids) => (
              <Select
                {...ids}
                placeholder="All countries"
                value={country}
                disabled={!options.data}
                options={(options.data?.countries ?? []).map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(event) => {
                  setCountry(event.target.value);
                  setPage(1);
                }}
              />
            )}
          </Field>
          <Field label="Industry">
            {(ids) => (
              <Select
                {...ids}
                placeholder="All industries"
                value={industry}
                disabled={!options.data}
                options={(options.data?.industries ?? []).map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(event) => {
                  setIndustry(event.target.value);
                  setPage(1);
                }}
              />
            )}
          </Field>
          <Field label="Employment type">
            {(ids) => (
              <Select
                {...ids}
                placeholder="All employment types"
                value={employmentType}
                disabled={!options.data}
                options={(options.data?.employment_types ?? []).map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(event) => {
                  setEmploymentType(event.target.value);
                  setPage(1);
                }}
              />
            )}
          </Field>
        </div>
        <div className={styles.filterActions}>
          <Checkbox
            label="Highlighted jobs only"
            checked={highlightedOnly}
            onChange={(event) => {
              setHighlightedOnly(event.target.checked);
              setPage(1);
            }}
          />
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </div>
        {options.isError ? (
          <ErrorState
            message="The filter choices could not be loaded."
            onRetry={() => {
              void options.refetch();
            }}
          />
        ) : null}
      </section>
      {showHighlights && highlights.isPending ? (
        <Spinner label="Loading highlighted jobs" />
      ) : null}
      {showHighlights && highlights.isError ? (
        <ErrorState
          message="Highlighted jobs could not be loaded."
          onRetry={() => {
            void highlights.refetch();
          }}
        />
      ) : null}
      {showHighlights && highlights.data && highlights.data.total > 0 ? (
        <section className={styles.highlights} aria-labelledby="highlighted-jobs-title">
          <div className={styles.toolbar}>
            <div>
              <span className={styles.eyebrow}>IN THE SPOTLIGHT</span>
              <h2 id="highlighted-jobs-title">Highlighted jobs</h2>
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setHighlightedOnly(true);
                setPage(1);
              }}
            >
              See all highlighted jobs
            </Button>
          </div>
          <div className={styles.grid}>
            {highlights.data.items.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </section>
      ) : null}
      <div className={styles.toolbar}>
        <h2>Open opportunities {jobs.data ? <span>({jobs.data.total})</span> : null}</h2>
      </div>
      {jobs.isPending ? (
        <Spinner label="Loading opportunities" />
      ) : jobs.isError ? (
        <ErrorState
          message="The jobs could not be loaded."
          onRetry={() => {
            void jobs.refetch();
          }}
        />
      ) : jobs.data.items.length === 0 ? (
        <EmptyState
          icon={<BriefcaseBusiness />}
          title="No open roles found"
          body={
            hasFilters
              ? 'Try another search or clear the filters to see more opportunities.'
              : 'New opportunities will appear here as they become available.'
          }
        />
      ) : (
        <>
          <div className={styles.grid}>
            {jobs.data.items.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
          <Pagination
            page={page}
            perPage={12}
            total={jobs.data.total}
            label="jobs"
            onChange={setPage}
          />
        </>
      )}
    </PublicCareersLayout>
  );
}

function HighlightBadge() {
  return (
    <span className={styles.highlightBadge}>
      <Star size={13} aria-hidden="true" /> Highlighted
    </span>
  );
}

function PublishedDate({ value }: { value: string | null | undefined }) {
  if (!value) return null;
  return (
    <p className={styles.publishedDate}>
      Published{' '}
      <time dateTime={value}>
        {new Intl.DateTimeFormat('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(value))}
      </time>
    </p>
  );
}

function JobCard({ job }: { job: Vacancy }) {
  return (
    <article className={cx(styles.card, job.highlighted && styles.cardHighlighted)}>
      <div className={styles.cardTop}>
        <span className={styles.eyebrow}>{job.industry}</span>
        {job.highlighted ? <HighlightBadge /> : null}
      </div>
      <h3>
        <Link to={`/jobs/${job.id}`}>
          {job.title}
          <ArrowUpRight size={20} aria-hidden="true" />
        </Link>
      </h3>
      <p className={styles.location}>
        <MapPin size={16} aria-hidden="true" />
        {[job.location, job.country].filter(Boolean).join(', ')}
      </p>
      <p>{job.employment_type}</p>
      <PublishedDate value={job.published_at} />
      {job.salary ? <strong>{job.salary}</strong> : null}
      <Link className={styles.jobLink} to={`/jobs/${job.id}`}>
        View role and apply →
      </Link>
      <JobLinkActions job={job} />
    </article>
  );
}

export function PublicJobPage() {
  const { id = '' } = useParams();
  const job = useQuery({
    queryKey: ['public-job', id],
    queryFn: () => request<Vacancy>(`/api/v1/public/jobs/${id}`, { anonymous: true }),
    retry: false,
  });
  return (
    <PublicCareersLayout>
      <Link className={styles.back} to="/jobs">
        <ArrowLeft size={16} /> All opportunities
      </Link>
      {job.isPending ? (
        <Spinner label="Loading job" />
      ) : job.isError ? (
        <ErrorState
          message={
            job.error instanceof ApiError && job.error.status === 404
              ? 'This job is no longer accepting applications. Browse the other open roles.'
              : 'This job could not be loaded.'
          }
          onRetry={() => {
            void job.refetch();
          }}
        />
      ) : (
        <>
          <section className={styles.jobHead}>
            <span className={styles.eyebrow}>{job.data.industry}</span>
            {job.data.highlighted ? <HighlightBadge /> : null}
            <h1>{job.data.title}</h1>
            <p>
              {job.data.employment_type} · {job.data.country}
            </p>
            <PublishedDate value={job.data.published_at} />
            <div className={styles.jobActions}>
              <JobLinkActions key={job.data.id} job={job.data} />
            </div>
          </section>
          <div className={styles.detailGrid}>
            <section className={styles.description}>
              <VacancyDetails job={job.data} publicView />
            </section>
            <PublicApplicationForm key={id} job={job.data} />
          </div>
        </>
      )}
    </PublicCareersLayout>
  );
}
